import React from 'react';
import { Box, Stack, Button } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DownloadIcon from '@mui/icons-material/Download';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useChartCapture, usePdfGeneration, usePrivacyToggle, useAlert, type AlertMessage } from '../../../hooks';
import useTrialExpirationHandler from '../../../hooks/useTrialExpirationHandler';
import ActionsCard from '../../ActionsCard';
import CardTitle from '../../common/CardTitle';
import ResultsPDF from '../../ResultsPDF';
import { SimulationData } from '../../../hooks/useSimulationResults';

export interface ResultsActionsProps {
  simulation: SimulationData;
  effectiveSimId: string | null;
  isCreator: boolean;
  loading: boolean;
  displayResults: any;
  formData: {
    content: string;
    platform: string;
    sender: string;
    sector: string;
  };
  forecast: any;
  reputationInfo: {
    level: string;
    color: string;
  };
  uiScore: number;
  onSimulationUpdate: (updates: Partial<SimulationData>) => void;
  showSuccess?: (message: string) => void;
  showAlert?: (alert: AlertMessage) => void;
  variant: 'compact' | 'full';
}

const ResultsActions: React.FC<ResultsActionsProps> = ({
  simulation,
  effectiveSimId,
  isCreator,
  loading,
  displayResults,
  formData,
  forecast,
  reputationInfo,
  uiScore,
  onSimulationUpdate,
  showSuccess: propShowSuccess,
  showAlert: propShowAlert,
  variant
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const alertHook = useAlert();
  // Use props if provided, otherwise fall back to local hook
  const showAlert = propShowAlert || alertHook.showAlert;
  const showSuccess = propShowSuccess || alertHook.showSuccess;
  const { handleApiError } = useTrialExpirationHandler();
  const { chartImages } = useChartCapture();
  const { pdfLoading, generatePdf } = usePdfGeneration({ 
    onError: showAlert 
  });

  // Privacy toggle hook
  const privacyToggle = usePrivacyToggle({
    itemId: effectiveSimId,
    currentPrivacyState: simulation?.isPrivate || false,
    apiEndpoint: 'simulation',
    onSuccess: showSuccess,
    onError: showAlert,
    handleApiError
  });

  // Edit simulation handler
  const handleEditSimulation = () => {
    navigate(`/simulations/create?id=${effectiveSimId}`);
  };

  // Navigate to dashboard
  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  // Handle PDF download - generate PDF only when button is clicked
  const handleDownloadPDF = async () => {
    if (!simulation || !displayResults) return;
    
    const doc = (
      <ResultsPDF
        simulation={simulation}
        displayResults={displayResults}
        formData={formData}
        forecast={forecast}
        reputationInfo={reputationInfo}
        uiScore={uiScore}
        chartImages={chartImages}
        user={user || undefined}
      />
    );
    
    // Sanitize platform name and create filename
    const sanitizedPlatform = formData.platform.replace(/[^a-zA-Z0-9]/g, '');
    const timestamp = Date.now();
    const filename = `WaveScoop-results-${sanitizedPlatform}-${timestamp}.pdf`;
    
    console.log('PDF Download: Generating with filename:', filename);
    await generatePdf(doc, filename);
  };

  // Handle privacy toggle
  const handlePrivacyToggle = async () => {
    const success = await privacyToggle.togglePrivacy();
    if (success) {
      onSimulationUpdate({
        isPrivate: !simulation.isPrivate
      });
    }
  };

  if (variant === 'compact') {
    return (
      <Box sx={{ mb: 4 }}>
        <ActionsCard variant="compact" elevation={2}>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ width: '100%' }}
          >
            <Button 
              variant="contained" 
              color="primary"
              startIcon={<EditIcon />}
              onClick={handleEditSimulation}
              disabled={loading}
              size="small"
              sx={{ 
                minWidth: { xs: '100%', sm: '130px' },
                maxWidth: { xs: '100%', sm: '130px' },
                width: { xs: '100%', sm: '130px' },
                height: '36px',
                padding: '6px 16px',
                whiteSpace: 'nowrap'
              }}
            >
              Edit
            </Button>
            <Button 
              variant="outlined"
              color="secondary"
              startIcon={<DownloadIcon />}
              disabled={pdfLoading || loading}
              size="small"
              onClick={handleDownloadPDF}
              sx={{ 
                minWidth: { xs: '100%', sm: '130px' },
                maxWidth: { xs: '100%', sm: '130px' },
                width: { xs: '100%', sm: '130px' },
                height: '36px',
                padding: '6px 16px',
                whiteSpace: 'nowrap'
              }}
            >
              {pdfLoading ? 'Generating...' : 'Download PDF'}
            </Button>
            {isCreator && (
              <Button
                variant="outlined"
                color="primary"
                onClick={handlePrivacyToggle}
                disabled={loading || privacyToggle.loading}
                size="small"
                startIcon={simulation.isPrivate ? (
                  <LockOpenIcon fontSize="small" />
                ) : (
                  <LockOutlinedIcon fontSize="small" />
                )}
                sx={{ 
                  minWidth: { xs: '100%', sm: '130px' },
                  maxWidth: { xs: '100%', sm: '130px' },
                  width: { xs: '100%', sm: '130px' },
                  height: '36px',
                  padding: '6px 16px',
                  whiteSpace: 'nowrap'
                }}
              >
                {simulation.isPrivate ? 'Make Public' : 'Make Private'}
              </Button>
            )}
            {/* Show link back to Idea Draft when present */}
            {simulation?.ideaDraft && (
              <Button 
                size="small" 
                variant="outlined" 
                onClick={() => window.open(`/ideas/${simulation.ideaDraft?.idea?.id}/results?focusDraft=${simulation.ideaDraft?.id}`, '_blank', 'noopener,noreferrer')}
                sx={{ 
                  minWidth: { xs: '100%', sm: '130px' },
                  maxWidth: { xs: '100%', sm: '150px' },
                  width: { xs: '100%', sm: 'auto' },
                  height: '36px',
                  padding: '6px 16px',
                  whiteSpace: 'nowrap'
                }}
              >
                View Original Idea
              </Button>
            )}
          </Stack>
        </ActionsCard>
      </Box>
    );
  }

  return (
    <ActionsCard
      variant="full"
      titleComponent={
        <CardTitle 
          title="Actions" 
          tooltip="What would you like to do next?"
        />
      }
    >
      {/* Single row with all actions on desktop, stacked on mobile */}
      <Stack 
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 2, md: 2 }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        sx={{ width: '100%' }}
      >
        {/* Privacy Toggle */}
        {isCreator && (
          <Button
            variant="outlined"
            color="primary"
            onClick={handlePrivacyToggle}
            disabled={loading || privacyToggle.loading}
            startIcon={simulation.isPrivate ? (
              <LockOpenIcon />
            ) : (
              <LockOutlinedIcon />
            )}
            sx={{ 
              flex: 1,
              py: { xs: 1.5, sm: 1.2 },
              fontSize: { xs: '1rem', sm: '0.875rem' },
              '&:hover': {
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            {simulation.isPrivate ? 'Make Public' : 'Make Private'}
          </Button>
        )}
        
        {/* Action Buttons */}
        <Button 
          variant="contained"
          color="primary"
          startIcon={<EditIcon />}
          onClick={handleEditSimulation}
          disabled={loading}
          sx={{ 
            flex: 1,
            py: { xs: 1.5, sm: 1.2 },
            fontSize: { xs: '1rem', sm: '0.875rem' },
            boxShadow: '0 2px 8px rgba(10, 124, 160, 0.25)',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(10, 124, 160, 0.35)',
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          Edit Simulation
        </Button>
        
        <Button 
          variant="outlined"
          color="primary"
          startIcon={<DownloadIcon />}
          disabled={pdfLoading || loading}
          onClick={handleDownloadPDF}
          sx={{ 
            width: '100%',
            flex: 1,
            py: { xs: 1.5, sm: 1.2 },
            fontSize: { xs: '1rem', sm: '0.875rem' },
            '&:hover': {
              transform: 'translateY(-1px)',
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          {pdfLoading ? 'Generating PDF...' : 'Download PDF'}
        </Button>
        
        <Button 
          variant="outlined"
          color="primary"
          startIcon={<RestartAltIcon />}
          onClick={handleBackToDashboard}
          disabled={loading}
          sx={{ 
            flex: 1,
            py: { xs: 1.5, sm: 1.2 },
            fontSize: { xs: '1rem', sm: '0.875rem' },
            '&:hover': {
              transform: 'translateY(-1px)',
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          Back to Dashboard
        </Button>
      </Stack>

      {/* Show link back to Idea Draft when present */}
      {simulation?.ideaDraft && (
        <Box sx={{ mt: 2 }}>
          <Button 
            variant="outlined"
            color="primary"
            onClick={() => window.open(`/ideas/${simulation.ideaDraft?.idea?.id}/results?focusDraft=${simulation.ideaDraft?.id}`, '_blank', 'noopener,noreferrer')}
            disabled={loading}
            fullWidth
            sx={{ 
              py: { xs: 1.5, sm: 1.2 },
              fontSize: { xs: '1rem', sm: '0.875rem' },
              '&:hover': {
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            View Original Idea
          </Button>
        </Box>
      )}
    </ActionsCard>
  );
};

export default ResultsActions;